// errorLog.js
//
// Minimal structured error log for launch-day diagnostics, before a
// proper APM (Sentry, LogRocket, etc.) is wired up. Goals:
//
//   * Stable session id so multiple errors from the same user can be
//     grouped after the fact.
//   * Ring buffer of the last N entries so we keep recent context
//     (a user might hit three errors in a row and we want all three).
//   * Lightweight enough that calling it from inside a render error
//     handler can't itself throw and worsen the situation.
//   * No external services — everything lives in sessionStorage so a
//     user / support agent can read it without leaving the browser.
//
// Access patterns:
//   * `logError(err, context)` — call from anywhere on error paths.
//   * `getRecentErrors()` — returns the current ring buffer for
//     display in the UI or for `window.__dfErrorLog()` (mounted below
//     so support can ask the user to paste it from the console).
//
// Storage layout:
//   sessionStorage.lf_session_id    — stable 8-char id per tab
//   sessionStorage.lf_error_log_v1  — JSON array of last 20 entries

const SESSION_KEY = 'lf_session_id';
const LOG_KEY     = 'lf_error_log_v1';
const MAX_ENTRIES = 20;

// 8-char hex per tab. Long enough to disambiguate user reports,
// short enough to be readable when the user reads it out loud.
function newSessionId() {
  return Math.random().toString(16).slice(2, 10);
}

export function getSessionId() {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = newSessionId();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    // Private browsing / disabled storage — return an ephemeral id so
    // logs still carry an identifier within the page lifetime.
    return 'no-storage';
  }
}

function readBuffer() {
  try {
    const raw = sessionStorage.getItem(LOG_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function writeBuffer(buf) {
  try {
    sessionStorage.setItem(LOG_KEY, JSON.stringify(buf));
  } catch {
    // Storage full — drop oldest half and retry once.
    try {
      sessionStorage.setItem(LOG_KEY, JSON.stringify(buf.slice(-MAX_ENTRIES / 2)));
    } catch { /* give up — better to lose logs than crash the page */ }
  }
}

/**
 * Append a structured entry to the ring buffer.
 * Safe to call from inside an error path — never throws.
 *
 * @param {unknown}  err      — Error instance OR thrown value (string/number/etc.)
 * @param {object}   context  — { source, ...extras }. Keep small; gets serialised.
 */
export function logError(err, context = {}) {
  try {
    const entry = {
      ts:        new Date().toISOString(),
      session:   getSessionId(),
      source:    context.source || 'unknown',
      message:   err?.message || String(err) || 'no message',
      stack:     (err?.stack || '').slice(0, 2000),
      url:       (typeof window !== 'undefined' && window.location?.href) || '',
      // Allow callers to attach a few key-value pairs; cap the size.
      meta:      Object.fromEntries(
                   Object.entries(context)
                     .filter(([k]) => k !== 'source')
                     .slice(0, 8)
                     .map(([k, v]) => [k, String(v).slice(0, 200)]),
                 ),
    };
    const buf = readBuffer();
    buf.push(entry);
    while (buf.length > MAX_ENTRIES) buf.shift();
    writeBuffer(buf);
  } catch {
    // Never let logging propagate failure.
  }
}

/** Return the most-recent N entries (oldest first). */
export function getRecentErrors() {
  return readBuffer();
}

/** Wipe the buffer (e.g. after the user copies it to support). */
export function clearErrors() {
  try { sessionStorage.removeItem(LOG_KEY); } catch { /* ignore */ }
}

/** Formatted plain-text dump suitable for pasting into email / chat. */
export function formatErrorReport() {
  const entries = getRecentErrors();
  const head = [
    'DocketFlow error report',
    '═══════════════════════',
    `Session:   ${getSessionId()}`,
    `Generated: ${new Date().toISOString()}`,
    `URL:       ${window.location?.href || ''}`,
    `Agent:     ${navigator?.userAgent || ''}`,
    `Entries:   ${entries.length}`,
    '',
  ].join('\n');
  if (entries.length === 0) return head + '(no errors captured)';
  const body = entries.map((e, i) => [
    `── #${i + 1} ── ${e.ts} ── ${e.source}`,
    `${e.message}`,
    e.meta && Object.keys(e.meta).length ? `meta: ${JSON.stringify(e.meta)}` : '',
    e.stack ? `${e.stack}` : '',
  ].filter(Boolean).join('\n')).join('\n\n');
  return head + body;
}

// ── Console-accessible helper ──────────────────────────────────────
// `window.__dfErrorLog()` returns the formatted report. Support can
// ask a stuck user to "open DevTools → console → type window.__dfErrorLog()"
// and paste the output. Zero UI surface so it can never break the
// page itself.
if (typeof window !== 'undefined') {
  // eslint-disable-next-line no-underscore-dangle
  window.__dfErrorLog = formatErrorReport;
  // eslint-disable-next-line no-underscore-dangle
  window.__dfClearErrorLog = clearErrors;
}
